import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signinFormDefaultValue, signinFormType, getSigninFormValidationSchema } from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { useAuthStore } from '@/state/store/auth';
import {
  REMEMBER_ME_KEY,
  REMEMBERED_EMAIL_KEY,
  setRememberMePreference,
} from '@/state/store/auth/auth-storage';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useSigninEmail } from '../../hooks/use-auth';
import { Captcha, ErrorAlert, useCaptcha } from '@/components/core';
import { useEffect, useRef, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { LOGIN_COLORS } from '../../constants/login-theme';
import { cn } from '@/lib/utils';
import { getPostAuthPath } from '@/lib/auth/org-selection';

const FAILED_ATTEMPTS_KEY = 'signin-failed-attempts';
const MAX_ATTEMPTS_BEFORE_CAPTCHA = 3;

const loginInputClassName = cn(
  'h-12 rounded-xl border bg-[#FFFFFF] px-4 py-3 text-sm text-[#252728] shadow-none',
  'focus-visible:ring-1 focus-visible:ring-[#0066B2]',
  'placeholder:text-[#A6A8A9]',
  '[&:-webkit-autofill]:shadow-[0_0_0_1000px_#ffffff_inset]',
  '[&:-webkit-autofill]:[-webkit-text-fill-color:#252728]'
);

export const SigninEmail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, setTokens } = useAuthStore();
  const { handleError } = useErrorHandler();
  const [showPassword, setShowPassword] = useState(false);

  const failedAttemptsRef = useRef<number>(
    parseInt(sessionStorage.getItem(FAILED_ATTEMPTS_KEY) || '0', 10)
  );
  const [showCaptcha, setShowCaptcha] = useState(
    failedAttemptsRef.current >= MAX_ATTEMPTS_BEFORE_CAPTCHA
  );
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem(REMEMBER_ME_KEY) === 'true'
  );

  const rememberedEmail = rememberMe ? localStorage.getItem(REMEMBERED_EMAIL_KEY) || '' : '';

  const form = useForm({
    defaultValues: {
      ...signinFormDefaultValue,
      username: rememberedEmail,
    },
    resolver: zodResolver(getSigninFormValidationSchema(t)),
  });

  const { isPending, mutateAsync, isError } = useSigninEmail();

  const googleSiteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';
  const captchaEnabled = googleSiteKey !== '';
  const captchaType =
    import.meta.env.VITE_CAPTCHA_TYPE === 'reCaptcha' ? 'reCaptcha-v2-checkbox' : 'hCaptcha';

  const {
    code: captchaCode,
    captcha,
    reset: resetCaptcha,
  } = useCaptcha({
    siteKey: googleSiteKey,
    type: captchaType,
  });

  const { isValid } = form.formState;

  useEffect(() => {
    if (!isValid && captchaCode) resetCaptcha();
  }, [captchaCode, isValid, resetCaptcha]);

  const onSubmitHandler = async (values: signinFormType) => {
    try {
      const res = await mutateAsync({
        username: values.username,
        password: values.password,
        ...(showCaptcha && captchaCode ? { captchaCode } : {}),
      });

      // Must run before login/MFA so auth persistence uses the correct storage.
      setRememberMePreference(rememberMe, values.username);

      failedAttemptsRef.current = 0;
      sessionStorage.removeItem(FAILED_ATTEMPTS_KEY);
      setShowCaptcha(false);

      if (res.enable_mfa)
        return navigate(
          `/verify-mfa?mfa_id=${res?.mfaId}&mfa_type=${res?.mfaType}&user_name=${values.username}`
        );

      login(res.access_token ?? '', res.refresh_token ?? '');
      setTokens({ accessToken: res.access_token ?? '', refreshToken: res.refresh_token ?? '' });
      navigate(getPostAuthPath());
    } catch (error) {
      failedAttemptsRef.current += 1;
      sessionStorage.setItem(FAILED_ATTEMPTS_KEY, failedAttemptsRef.current.toString());

      if (failedAttemptsRef.current >= MAX_ATTEMPTS_BEFORE_CAPTCHA) {
        setShowCaptcha(true);
      }

      resetCaptcha();
      handleError(error);
    }
  };

  const isCaptchaRequired = showCaptcha && captchaEnabled;
  const PasswordToggleIcon = showPassword ? EyeSlashIcon : EyeIcon;

  return (
    <div className="w-full">
      <ErrorAlert
        isError={isError}
        title={t('INVALID_CREDENTIALS')}
        message={t('EMAIL_PASSWORD_NOT_VALID')}
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmitHandler)} className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel
                  className="text-sm font-semibold leading-5"
                  style={{ color: LOGIN_COLORS.textLabel }}
                >
                  {t('EMAIL_ADDRESS')}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('ENTER_YOUR_EMAIL')}
                    className={loginInputClassName}
                    style={{ borderColor: LOGIN_COLORS.inputBorder }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel
                  className="text-sm font-semibold leading-5"
                  style={{ color: LOGIN_COLORS.textLabel }}
                >
                  {t('PASSWORD')}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('ENTER_YOUR_PASSWORD')}
                      className={cn(loginInputClassName, 'pr-11')}
                      style={{ borderColor: LOGIN_COLORS.inputBorder }}
                      {...field}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 z-10 -translate-y-1/2"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <PasswordToggleIcon
                        className="size-5"
                        style={{ color: LOGIN_COLORS.textPlaceholder }}
                      />
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
                className="size-4 rounded-[3px] border bg-[#FFFFFF] shadow-none data-[state=checked]:border-transparent data-[state=checked]:bg-[#0066B2] data-[state=checked]:text-[#FFFFFF]"
                style={{ borderColor: LOGIN_COLORS.inputBorder }}
              />
              <label
                htmlFor="remember-me"
                className="cursor-pointer text-sm font-normal leading-5"
                style={{ color: LOGIN_COLORS.textPrimary }}
              >
                {t('REMEMBER_ME')}
              </label>
            </div>
            <Link
              to="/forgot-password"
              className="text-sm font-medium leading-5 hover:underline"
              style={{ color: LOGIN_COLORS.primary }}
            >
              {t('FORGOT_PASSWORD')}
            </Link>
          </div>

          {isCaptchaRequired && (
            <div className="my-1">
              <Captcha {...captcha} theme="light" size="normal" />
            </div>
          )}

          <Button
            type="submit"
            className="mt-1 h-12 w-full rounded-xl border-0 text-sm font-semibold shadow-none hover:opacity-90"
            style={{ backgroundColor: LOGIN_COLORS.primary, color: LOGIN_COLORS.pureWhite }}
            disabled={isPending || (isCaptchaRequired && !captchaCode)}
          >
            {t('SIGN_IN')}
          </Button>
        </form>
      </Form>
    </div>
  );
};
